import { request } from './api'
import { prefetchAsset } from './assets'
import { worldCatalog } from './catalog'
import { prewarmMap } from './mapWarmCache'
import { session } from './session'
import type { AppRoute } from './types'

const recentlyPrefetched = new Map<string, number>()
const PREFETCH_REUSE_MS = 10_000

function read(path: string): Promise<unknown> {
  return request<unknown>(path)
}

function villageBase(route: AppRoute): string | null {
  if (!route.worldId || !route.villageId) return null
  return `/api/v2/worlds/${route.worldId}/villages/${route.villageId}`
}

function prefetchRouteArt(route: AppRoute): void {
  if (route.name === 'building' && route.buildingType) {
    prefetchAsset(`building.${route.buildingType}.hero.level.0`)
    return
  }
  const art: Partial<Record<AppRoute['name'], string>> = {
    'world-dashboard': 'world.dashboard.hero',
    village: 'village.background',
    buildings: 'building.catalog.hero',
    construction: 'building.construction.hero',
    army: 'military.army.hero',
    movements: 'military.movements.hero',
    market: 'market.hero',
    alliance: 'alliance.hero',
    quests: 'content.journal.hero',
    inventory: 'inventory.hero',
    rankings: 'ranking.hero',
    victory: 'victory.hero',
    notifications: 'notifications.hero',
    messages: 'messages.hero',
  }
  prefetchAsset(art[route.name])
}

export function prefetchNavigation(route: AppRoute): void {
  const key = `${route.path}?${route.query.toString()}`
  const now = Date.now()
  for (const [cachedKey, timestamp] of recentlyPrefetched) {
    if (now - timestamp >= PREFETCH_REUSE_MS)
      recentlyPrefetched.delete(cachedKey)
  }
  const previous = recentlyPrefetched.get(key)
  if (previous !== undefined && now - previous < PREFETCH_REUSE_MS) return
  while (recentlyPrefetched.size >= 64) {
    const oldest = recentlyPrefetched.keys().next()
    if (oldest.done) break
    recentlyPrefetched.delete(oldest.value)
  }
  recentlyPrefetched.set(key, now)
  prefetchRouteArt(route)

  const tasks: Promise<unknown>[] = []
  const worldId = route.worldId
  const base = villageBase(route)

  if (worldId && ['world-dashboard'].includes(route.name)) {
    tasks.push(
      read(`/api/v2/worlds/${worldId}/dashboard`),
      worldCatalog(worldId),
    )
  }

  if (worldId && route.name === 'map' && session.account?.accountId) {
    tasks.push(prewarmMap(session.account.accountId, worldId, true))
  }

  if (base) {
    if (route.name === 'village') {
      tasks.push(
        read(base),
        read(`${base}/resources`),
        read(`${base}/buildings`),
        read(`${base}/building-orders?limit=20`),
        worldCatalog(route.worldId ?? ''),
      )
    } else if (route.name === 'dashboard') {
      tasks.push(
        read(base),
        read(`${base}/resources`),
        read(`${base}/building-orders?limit=8`),
        read(`${base}/training-orders?limit=8`),
      )
    } else if (route.name === 'buildings') {
      tasks.push(
        read(`${base}/buildings`),
        read(`${base}/building-orders?limit=50`),
        worldCatalog(route.worldId ?? ''),
      )
    } else if (route.name === 'construction') {
      tasks.push(
        read(`${base}/building-orders?limit=100`),
        read(`${base}/buildings`),
        worldCatalog(route.worldId ?? ''),
      )
    } else if (route.name === 'army') {
      tasks.push(read(`${base}/units`), read(`${base}/training-orders`))
    } else if (
      ['training', 'research', 'smithy', 'market', 'nobility'].includes(
        route.name,
      )
    ) {
      tasks.push(
        read(`${base}/buildings`),
        read(base),
        worldCatalog(route.worldId ?? ''),
      )
      if (route.name === 'training')
        tasks.push(read(`${base}/units`), read(`${base}/training-orders`))
      if (route.name === 'research') tasks.push(read(`${base}/research`))
      if (route.name === 'smithy') tasks.push(read(`${base}/smithy`))
      if (route.name === 'market') tasks.push(read(`${base}/market-state`))
      if (route.name === 'nobility') tasks.push(read(`${base}/nobility-state`))
    } else if (route.name === 'building') {
      tasks.push(
        read(`${base}/buildings`),
        read(base),
        worldCatalog(route.worldId ?? ''),
      )
      const module = route.query.get('module')
      if (module === 'training')
        tasks.push(read(`${base}/units`), read(`${base}/training-orders`))
      if (module === 'research') tasks.push(read(`${base}/research`))
      if (module === 'smithy') tasks.push(read(`${base}/smithy`))
      if (module === 'market') tasks.push(read(`${base}/market-state`))
      if (module === 'nobility') tasks.push(read(`${base}/nobility-state`))
    }
  }

  if (worldId && route.name === 'movements')
    tasks.push(read(`/api/v2/worlds/${worldId}/movement-center`))
  if (worldId && route.name === 'reports')
    tasks.push(
      read(
        `/api/v2/worlds/${worldId}/report-center?q=&favorite=false&archived=false&limit=100`,
      ),
    )
  if (worldId && route.name === 'messages')
    tasks.push(read(`/api/v2/worlds/${worldId}/messages?limit=50`))
  if (worldId && route.name === 'alliance')
    tasks.push(
      read(`/api/v2/worlds/${worldId}/alliances/me`),
      worldCatalog(worldId),
    )
  if (worldId && route.name === 'quests')
    tasks.push(
      read(`/api/v2/worlds/${worldId}/content/journal`),
      read('/api/v2/content/catalog?type=QUEST'),
      read('/api/v2/content/catalog?type=CAMPAIGN'),
      read('/api/v2/content/catalog?type=DAILY_REWARD'),
    )
  if (worldId && route.name === 'inventory')
    tasks.push(read(`/api/v2/worlds/${worldId}/content/inventory`))
  if (worldId && route.name === 'rankings') {
    const type = route.query.get('type') ?? 'PLAYER'
    tasks.push(
      read(
        `/api/v2/worlds/${worldId}/rankings/${encodeURIComponent(type)}?limit=100`,
      ),
    )
  }
  if (worldId && route.name === 'victory')
    tasks.push(
      read(`/api/v2/worlds/${worldId}/victory`),
      read(`/api/v2/worlds/${worldId}/chronicle?limit=100`),
    )
  if (route.name === 'notifications') {
    const query = worldId
      ? `?world=${encodeURIComponent(worldId)}&limit=100`
      : '?limit=100'
    tasks.push(read(`/api/v2/notifications${query}`))
  }

  if (tasks.length > 0) void Promise.allSettled(tasks)
}
